import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import api from '../services/api';

interface VendorVisitorInspectionReportData {
  token?: string;
  propertyAddress: string;
  agencyName: string;
  agencyBannerImageData?: string;
  agencyBannerImageType?: string;
  agencyBannerTitle?: string;
  summary: string;
  status: string;
  periodStart: string;
  periodEnd: string;
  metrics: {
    totalVisitors: number;
    feedbackCount: number;
    highInterestCount: number;
    contractRequestCount: number;
    applicationRequestCount: number;
    sessionCount: number;
  };
  attendees: Array<{
    _id?: string;
    name: string;
    email?: string;
    phone?: string;
    visitDate?: string;
    visitType?: string;
    feedback?: string;
    interestLevel?: string;
    priceFeedback?: string;
    followUpIntent?: string;
    objections?: string;
    keenBuyer?: boolean;
  }>;
}

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const getBannerSrc = (report: VendorVisitorInspectionReportData) => {
  if (!report.agencyBannerImageData || !report.agencyBannerImageType) return '';
  return `data:${report.agencyBannerImageType};base64,${report.agencyBannerImageData}`;
};

const shiftWeek = (periodStart: string, periodEnd: string, direction: -1 | 1) => {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);
  start.setDate(start.getDate() + direction * 7);
  end.setDate(end.getDate() + direction * 7);
  return {
    periodStart: toDateInputValue(start),
    periodEnd: toDateInputValue(end),
  };
};

const VendorVisitorInspectionReport: React.FC = () => {
  const { token, code } = useParams<{ agencySlug?: string; token?: string; code?: string }>();
  const [accessCode, setAccessCode] = useState(code || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<VendorVisitorInspectionReportData | null>(null);

  const unlockByCode = useCallback(async (period?: { periodStart: string; periodEnd: string }) => {
    if (!code || code.length !== 6) return;
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/agencies/vendor/visitor-inspection-report/code/${code}`, { params: period });
      setReport(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load visitor inspection report');
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    if (code && code.length === 6) {
      unlockByCode();
    }
  }, [code, unlockByCode]);

  const unlockByToken = async (period?: { periodStart: string; periodEnd: string }) => {
    if (!token || accessCode.length !== 6) return;
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.post(`/agencies/vendor/visitor-inspection-report/${token}`, { code: accessCode, ...(period || {}) });
      setReport(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load visitor inspection report');
    } finally {
      setLoading(false);
    }
  };

  const handleWeekChange = async (direction: -1 | 1) => {
    if (!report) return;
    const nextPeriod = shiftWeek(report.periodStart, report.periodEnd, direction);
    if (code) {
      await unlockByCode(nextPeriod);
    } else {
      await unlockByToken(nextPeriod);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;
  }

  const bannerSrc = report ? getBannerSrc(report) : '';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: { xs: 2, md: 5 } }}>
      <Container maxWidth="lg">
        <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)' }}>
          {report && (
            <Box sx={{ px: { xs: 2, md: 4 }, py: 3, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={3} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={2}>
                  {bannerSrc ? (
                    <Box
                      component="img"
                      src={bannerSrc}
                      alt={report.agencyName}
                      sx={{ maxHeight: 72, maxWidth: { xs: 180, sm: 260 }, objectFit: 'contain' }}
                    />
                  ) : (
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontWeight: 700 }}>
                      {report.agencyName?.slice(0, 1) || 'A'}
                    </Avatar>
                  )}
                  <Box>
                    <Typography variant="overline" color="text.secondary">Prepared by</Typography>
                    <Typography variant="h6">{report.agencyName}</Typography>
                    {report.agencyBannerTitle && (
                      <Typography variant="body2" color="text.secondary">{report.agencyBannerTitle}</Typography>
                    )}
                  </Box>
                </Box>
                <Chip label="Vendor Report" color="primary" variant="outlined" />
              </Box>
            </Box>
          )}

          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box textAlign="center" sx={{ mb: 3 }}>
              <Typography variant="h4" fontWeight={700} gutterBottom>Visitor Inspection Report</Typography>
              <Typography variant="h6" color="text.secondary">{report?.propertyAddress || ''}</Typography>
              {report && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Report period: {formatDate(report.periodStart)} to {formatDate(report.periodEnd)}
                </Typography>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!report && (
              <Box component="form" onSubmit={(event) => { event.preventDefault(); unlockByToken(); }} sx={{ maxWidth: 460, mx: 'auto', display: 'flex', gap: 2 }}>
                <TextField
                  label="Access Code"
                  value={accessCode}
                  onChange={(event) => setAccessCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: '0.35rem', fontWeight: 700 } }}
                  fullWidth
                />
                <Button variant="contained" disabled={!token || accessCode.length !== 6} onClick={() => unlockByToken()}>
                  Unlock
                </Button>
              </Box>
            )}

            {report && (
              <>
                <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
                  <Button startIcon={<ChevronLeft />} variant="outlined" onClick={() => handleWeekChange(-1)} disabled={loading}>
                    Previous Week
                  </Button>
                  <Button endIcon={<ChevronRight />} variant="outlined" onClick={() => handleWeekChange(1)} disabled={loading}>
                    Next Week
                  </Button>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    ['Visitors', report.metrics.totalVisitors],
                    ['Feedback Notes', report.metrics.feedbackCount],
                    ['High Interest', report.metrics.highInterestCount],
                    ['Contract Requests', report.metrics.contractRequestCount],
                    ['Sessions', report.metrics.sessionCount],
                  ].map(([label, value]) => (
                    <Grid item xs={6} md={2.4} key={label}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, height: '100%' }}>
                        <Typography variant="h5" fontWeight={700}>{value}</Typography>
                        <Typography variant="caption" color="text.secondary" textTransform="uppercase">{label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>Agent Summary</Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{report.summary}</Typography>
                </Paper>

                <Typography variant="h6" gutterBottom>Visitor Feedback Included in Vendor Report</Typography>
                {report.attendees.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No visitor feedback has been included for this period.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Visit</TableCell>
                          <TableCell>Interest</TableCell>
                          <TableCell>Feedback</TableCell>
                          <TableCell>Price / Objections</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.attendees.map((attendee, index) => (
                          <TableRow key={attendee._id || `feedback-${index}`} hover>
                            <TableCell>
                              <Typography fontWeight={600}>{index + 1}</Typography>
                            </TableCell>
                            <TableCell>{formatDate(attendee.visitDate)} {attendee.visitType ? `(${attendee.visitType.replace('_', ' ')})` : ''}</TableCell>
                            <TableCell>{attendee.interestLevel || (attendee.keenBuyer ? 'keen buyer' : '-')}</TableCell>
                            <TableCell>{attendee.feedback || '-'}</TableCell>
                            <TableCell>
                              {attendee.priceFeedback && <Typography variant="body2">Price: {attendee.priceFeedback}</Typography>}
                              {attendee.objections && <Typography variant="body2">Objections: {attendee.objections}</Typography>}
                              {!attendee.priceFeedback && !attendee.objections && '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default VendorVisitorInspectionReport;
